package com.klantenstop.web.pages.manageCompany.create

import com.klantenstop.web.data.models.domain.Category
import com.klantenstop.web.data.models.domain.Status
import com.klantenstop.web.pages.manageCompany.components.AddressValidationState
import com.klantenstop.web.utils.MVI
import com.klantenstop.web.utils.websiteRegex

interface CreateCompanyContract : MVI<CreateCompanyContract.State, CreateCompanyContract.Intent, CreateCompanyContract.Effect> {

    data class State(
        val categories: List<Category> = emptyList(),
        val name: String = "",
        val companyDescription: String = "",
        val phoneNumber: String = "",
        val emailAddress: String = "",
        val website: String = "",
        // Address fields
        val streetName: String = "",
        val streetNumber: String = "",
        val zipCode: String = "",
        val city: String = "",
        val country: String = "",
        val isValidatingAddress: Boolean = false,
        val addressValidationResult: AddressValidationState? = null,
        val addressLatitude: Double? = null,
        val addressLongitude: Double? = null,
        // End address fields
        val selectedCategory: Category? = null,
        val selectedStatusType: Status.Type = Status.Type.Unknown,
        val statusDescription: String = "",
        val showStatusDescription: Boolean = false,
        val hasAttemptedSave: Boolean = false,
        val callOnRoute: Boolean = false
    ) {
        private val nameChanged = name.isNotBlank()
        private val phoneNumberChanged = phoneNumber.isNotBlank()
        private val emailAddressChanged = emailAddress.isNotBlank()
        private val websiteChanged = website.isNotBlank()
        private val companyDescriptionChanged = companyDescription.isNotBlank()
        private val selectedCategoryChanged = selectedCategory != null
        private val statusDescriptionChanged = statusDescription.isNotBlank()
        private val addressChanged = streetName.isNotBlank() || streetNumber.isNotBlank() || 
                                     zipCode.isNotBlank() || city.isNotBlank() || country.isNotBlank()

        val isNameValid: Boolean = name.isNotBlank()
        val isPhoneNumberValid: Boolean = true // Optional field
        val isEmailAddressValid: Boolean = emailAddress.isBlank() || emailAddress.contains("@")
        val isWebsiteValid: Boolean = website.isBlank() || website.matches(websiteRegex)
        val isCategoryValid: Boolean = selectedCategory != null
        val isAddressComplete: Boolean = streetName.isNotBlank() && streetNumber.isNotBlank() && 
                                         zipCode.isNotBlank() && city.isNotBlank() && country.isNotBlank()
        val isAddressValidated: Boolean = addressValidationResult is AddressValidationState.Success

        val hasChanged: Boolean = nameChanged ||
                phoneNumberChanged ||
                emailAddressChanged ||
                websiteChanged ||
                companyDescriptionChanged ||
                selectedCategoryChanged ||
                statusDescriptionChanged ||
                addressChanged
    }

    sealed class Intent {
        data object LoadInitialData : Intent()

        data class UpdateName(val name: String) : Intent()
        data class UpdateDescription(val description: String) : Intent()
        data class UpdatePhoneNumber(val phoneNumber: String) : Intent()
        data class UpdateEmailAddress(val emailAddress: String) : Intent()
        data class UpdateWebsite(val website: String) : Intent()
        data class UpdateCompanyStatus(val companyStatus: Status.Type) : Intent()
        data class UpdateCompanyStatusDescription(val statusDescription: String) : Intent()
        data object ToggleStatusDescriptionVisibility : Intent()
        data class UpdateCategory(val companyCategory: Category?) : Intent()
        
        // Address intents
        data class UpdateStreetName(val streetName: String) : Intent()
        data class UpdateStreetNumber(val streetNumber: String) : Intent()
        data class UpdateZipCode(val zipCode: String) : Intent()
        data class UpdateCity(val city: String) : Intent()
        data class UpdateCountry(val country: String) : Intent()
        data object ValidateAddress : Intent()

        data object Save : Intent()
        data object Cancel : Intent()
    }

    sealed class Effect {
        data class NavigateBack(val companyIdentifier: String? = null) : Effect()
    }
}
